<template>
  <v-container fluid class="px-6 px-sm-12">
    <PageHeader
      :header-text="voyageReport ? voyageReport.voyageNo : 'Voyage Details'"
      :header-icon="'mdi-ferry'"
      :disableSiteChange="true"
    />

    <div v-if="!detailsLoading && voyageReport">
      <div class="d-flex mb-8">
        <v-chip
          v-if="!detailsLoading"
          class="mx-2"
          :color="getStatusColour(voyageReport.status)"
          text-color="white"
          >{{ voyageReport.status | pascalToReadable }}</v-chip
        >
        <v-spacer></v-spacer>
        <div class="hidden-md-and-down">
          <v-btn
            v-if="voyageReport.status == 'InProgress'"
            :disabled="disableEndVoyage || detailsLoading"
            class="mx-2"
            v-bind="headerButtonSize"
            @click="openEndVoyageDialog"
          >
            <v-icon left>mdi-clock-check-outline</v-icon>End Voyage
          </v-btn>
          <v-btn
            v-if="voyageReport.status == 'Pending'"
            :disabled="detailsLoading"
            class="mx-2"
            v-bind="headerButtonSize"
            @click="openStartVoyageDialog"
          >
            <v-icon left>mdi-play</v-icon>Start Voyage
          </v-btn>
          <v-btn
            :disabled="detailsLoading"
            color="primary"
            v-bind="headerButtonSize"
            @click="openEditVoyageDialog"
          >
            <v-icon left>mdi-pencil</v-icon>Edit
          </v-btn>
        </div>
        <!-- Mobile/Tablet menu -->
        <v-menu offset-y left>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              v-bind="headerButtonSize"
              class="mr-2 hidden-lg-and-up"
              :disabled="detailsLoading"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              style="cursor: pointer"
              color="primary"
              @click="openEditVoyageDialog"
            >
              <v-list-item-icon>
                <v-icon class="mr-2">mdi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-capitalize">
                Edit
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              style="cursor: pointer"
              color="primary"
              v-if="voyageReport.status == 'Pending'"
              @click="openStartVoyageDialog"
            >
              <v-list-item-icon>
                <v-icon>mdi-play</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-capitalize">
                Start Voyage
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              style="cursor: pointer"
              color="primary"
              v-if="voyageReport.status == 'InProgress'"
              @click="openEndVoyageDialog"
            >
              <v-list-item-icon>
                <v-icon class="mr-2">mdi-clock-check-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-capitalize">
                End Voyage
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <!-- Dialogs -->
        <v-dialog v-model="startVoyageDialog" max-width="850px">
          <v-card>
            <StartVoyageForm
              :report="editReport"
              :onSubmitClick="editVoyageReport"
              :onCloseClick="closeStartVoyageDialog"
              :isSubmitting="isUpdateSubmitting"
            />
          </v-card>
        </v-dialog>
        <v-dialog v-model="detailsDialog" max-width="850px">
          <v-card>
            <v-card-text>
              <VoyageReportForm
                :edit="true"
                :report="editReport"
                :onSubmitClick="editVoyageReport"
                :onCloseClick="closeDetailsDialog"
                :onDeleteClick="deleteVoyageReport"
                :isSubmitting="isUpdateSubmitting"
                :bus="bus"
              />
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>

      <v-row>
        <v-col
          cols="12"
          sm="12"
          md="6"
          lg="6"
          xl="3"
          v-for="item in voyageDetailsArray"
          :key="item.label"
        >
          <v-card
            :min-height="voyageReport.voyageEndDateTimeLt ? '175px' : null"
          >
            <v-card-title class="primary--text">{{ item.label }}</v-card-title>
            <v-card-text>
              <v-list-item>
                <v-list-item-content class="text-start">
                  <v-list-item-title
                    class="text-h6 text-md-h4 text-lg-h4 text-xl-h4"
                  >
                    <v-icon
                      v-if="item.valuePrependIcon"
                      left
                      class="primary--text"
                      >{{ item.valuePrependIcon }}</v-icon
                    >
                    {{ item.value }}</v-list-item-title
                  >
                  <v-list-item-subtitle
                    class="text-h6"
                    v-if="voyageReport.voyageEndDateTimeLt"
                  >
                    <v-icon
                      v-if="item.subValuePrependIcon"
                      left
                      class="primary--text"
                      >{{ item.subValuePrependIcon }}</v-icon
                    >
                    {{ item.subValue }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-avatar class="elevation-4">
                  <v-icon color="primary">{{ item.icon }}</v-icon>
                </v-list-item-avatar>
              </v-list-item>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <div v-else class="details-container">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>

    <v-card class="mt-5" elevation="6">
      <v-tabs
        dark
        background-color="primary"
        v-model="tab"
        :show-arrows="showTabArrows"
      >
        <v-tabs-slider color="primary" />
        <v-tab href="#activities"> Activities </v-tab>
        <v-tab v-if="isClientConnectedToServer" href="#validation-report">
          Validation Report
          <v-chip
            v-if="invalidActivitiesCount > 0"
            v-text="invalidActivitiesCount"
            small
            class="mx-1"
            color="warning"
          />
          <v-chip v-else small class="mx-1" color="success" text-color="white">
            <v-icon small>mdi-check</v-icon>
          </v-chip>
        </v-tab>

        <v-tab href="#bulks">Bulk Transaction</v-tab>

        <v-tab href="#tanks"> Tanks </v-tab>

        <v-tab href="#passengers"> Passengers </v-tab>

        <v-tab href="#safetyobs" v-if="isSafetyObservationSiteEnabled"> Safety Observations </v-tab>

        <v-tab href="#cargo" v-if="isClientConnectedToServer"> Cargo </v-tab>

        <v-tab href="#planning" v-if="isClientConnectedToServer">
          Voyage Planning
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="activities">
          <Activities
            :voyageReportId="this.$route.params.id"
            :voyageReportIdc="this.$route.params.idc"
            :ecargoDataLoading="ecargoDataLoading"
            v-on:openEndVoyageDischargeDialog="openEndVoyageDischargeDialog()"
          />
        </v-tab-item>

        <v-tab-item value="validation-report" v-if="isClientConnectedToServer">
          <ValidationReport />
        </v-tab-item>

        <v-tab-item value="bulks">
          <bulk-transactions-overview />
        </v-tab-item>

        <v-tab-item value="tanks">
          <TankStatusGroupCard
            v-if="
              !detailsLoading &&
              vesselTankStatuses &&
              vesselTankStatuses.length > 0
            "
            :groupTitle="voyageReport.vesselName"
            groupIcon="mdi-car-coolant-level"
            :tanks="vesselTankStatuses"
            :onUpdateTankStatusClicked="openUpdateStatusDialog"
          />
          <v-row v-else justify="center">
            <InfoAlert message="No Tanks found for Vessel" />
          </v-row>
          <!-- Update Status Dialog -->
          <v-dialog v-model="updateTankStatusDialog" max-width="950px">
            <v-card>
              <TankStatusForm
                :status="tankStatus"
                :onSubmitClick="submitTankStatus"
                :onCloseClick="closeUpdateStatusDialog"
              />
            </v-card>
          </v-dialog>
        </v-tab-item>

        <v-tab-item value="passengers">
          <Passengers v-if="voyageReport" :voyageReport="voyageReport" />
        </v-tab-item>

        <v-tab-item value="safetyobs">
          <SafetyObservations v-if="voyageReport" :voyageReport="voyageReport" />
        </v-tab-item>

        <v-tab-item value="cargo" v-if="isClientConnectedToServer">
          <VoyageCargo
            :ecargoVoyage="voyageCargo"
            :ecargoDataLoading="ecargoDataLoading"
          />
        </v-tab-item>

        <v-tab-item value="planning" v-if="isClientConnectedToServer">
          <VoyagePlan
            :planningRequests="voyagePlanningRequests"
            :dataLoading="foresightDataLoading"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <ConfirmDialog
      v-model="endVoyageDialog"
      :title="endVoyageDialogTitle"
      :description="endVoyageDialogMessage"
      :onCloseClick="closeEndVoyageDialog"
      :onConfirmClick="endVoyageClicked"
    />
  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import ResponsiveSizeHelperMixin from '../mixins/ResponsiveSizeHelperMixin';
import StatusColourMixin from '../mixins/StatusColourMixin';
import TankStatusMixin from '../mixins/TankStatusMixin';
import PageHeader from '../components/ui/PageHeader.vue';
import Activities from '../components/vessel-activities/Activities.vue';
import ValidationReport from '../components/voyage-details/ValidationReport.vue';
import VoyageCargo from '../components/ecargo/VoyageCargo.vue';
import VoyageReportForm from '../components/voyage-reports/VoyageReportForm.vue';
import BulkTransactionsOverview from '../components/bulk_transactions/BulkTransactionsOverview.vue';
import TankStatusGroupCard from '../components/tanks/TankStatusGroupCard.vue';
import TankStatusForm from '../components/tanks/TankStatusForm.vue';
import StartVoyageForm from '../components/voyage-details/StartVoyageForm.vue';
import VoyagePlan from '../components/foresight/VoyagePlan.vue';
import ConfirmDialog from '../components/ui/ConfirmDialog.vue';
import InfoAlert from '../components/ui/InfoAlert.vue';
import dateTimeHelpers from '../mixins/DateTimeHelpers';
import Passengers from '../components/voyage-passengers/Passengers.vue';
import SafetyObservations from '../components/safety_observations/SafetyObservations.vue';

export default {
  components: {
    Activities,
    ValidationReport,
    VoyageCargo,
    VoyageReportForm,
    PageHeader,
    BulkTransactionsOverview,
    TankStatusGroupCard,
    TankStatusForm,
    StartVoyageForm,
    ConfirmDialog,
    VoyagePlan,
    InfoAlert,
    Passengers,
    SafetyObservations
  },
  mixins: [StatusColourMixin, ResponsiveSizeHelperMixin, TankStatusMixin],
  data() {
    return {
      detailsLoading: false,
      editReport: {},
      detailsDialog: false,
      startVoyageDialog: false,
      endVoyageDialog: false,
      isUpdateSubmitting: false,
      ecargoDataLoading: false,
      endVoyageDialogTitle: '',
      endVoyageDialogMessage: '',
      defaultEndVoyageDialogTitle: 'End Voyage',
      defaultEndVoyageDialogMessage: 'Are you sure you want to end this voyage?',
      foresightDataLoading: false,
      bus: new Vue(),
    };
  },
  created() {
    // load all data into vuex
    const { id, idc } = this.$route.params;
    this.detailsLoading = true;
    if (idc) {
      // Load unsynced
      this.$store.dispatch('dataSyncStore/loadOfflineVoyageReportDetails', idc)
        .finally(() => { this.detailsLoading = false; });
    } else if (id) {
      // load synced
      this.$store.dispatch('voyageReportsStore/loadVoyageReportDetails', id)
        .then(() => this.$store.dispatch('tanksStore/loadTankStatuses', this.getActiveSite.id))
        .finally(() => { this.detailsLoading = false; });

      this.$store.dispatch('voyageReportsStore/loadVoyageValidationReport', id);

      this.ecargoDataLoading = true;
      this.$store.dispatch('voyageReportsStore/loadCargoInformation', id)
        .then(() => {
          this.$store.dispatch('voyageReportsStore/loadVesselActivitiesCargo', id);
        })
        .then(() => {
          this.$store.dispatch('dataSyncStore/loadCargoLinesToBeSyncedForVoyage', id);
        })
        .finally(() => { this.ecargoDataLoading = false; });
      this.foresightDataLoading = true;
      this.$store.dispatch('voyageReportsStore/loadPortPlanningInformation', id)
        .finally(() => { this.foresightDataLoading = false; });
    }

    this.$store.dispatch('vesselsStore/loadVessels', this.getActiveSite.id);
    this.setEndVoyageDialogDefaultValues();
  },
  computed: {
    ...mapState(['voyageReportsStore', 'dataSyncStore']),
    ...mapGetters({
      getActiveSite: 'userStore/getActiveSite',
      getActiveSiteConfig: 'userStore/getActiveSiteConfig',
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getVoyageValidationReport: 'voyageReportsStore/getVoyageValidationReport',
      getSyncedAndUnSyncedVesselActivities: 'voyageReportsStore/getSyncedAndUnSyncedVesselActivities',
      getVesselTankStatuses: 'tanksStore/getVesselTanksWithCurrentStatus',
    }),
    tab: {
      set(tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } });
      },
      get() {
        return this.$route.query.tab;
      },
    },
    voyageReport() {
      return this.voyageReportsStore
        ? this.voyageReportsStore.voyageReportDetailsData : null;
    },
    voyageDetailsArray() {
      /* eslint-disable max-len */
      return [
        {
          value: this.voyageReport.voyageNo, subValue: null, label: 'Voyage', icon: 'mdi-pound-box',
        },
        {
          value: this.voyageReport.vesselName, subValue: null, label: 'Vessel', icon: 'mdi-ferry',
        },
        {
          value: this.voyageReport.initialPort, subValue: null, label: 'Initial Port', icon: 'mdi-map-marker',
        },
        {
          value: this.voyageReport.voyageStartDateTimeLt
            ? this.$options.filters.removeTAndSecondsAndFormatDate(this.voyageReport.voyageStartDateTimeLt)
            : 'Not Started',
          subValue: this.$options.filters.removeTAndSecondsAndFormatDate(this.voyageReport.voyageEndDateTimeLt),
          label: 'Voyage Start/End',
          icon: 'mdi-clock',
          valuePrependIcon: 'mdi-clock-start',
          subValuePrependIcon: 'mdi-clock-end',
        },
      ];
    },
    voyageCargo() {
      return this.voyageReportsStore ? this.voyageReportsStore.eCargoVoyageCargo : null;
    },
    voyagePlanningRequests() {
      return this.voyageReportsStore ? this.voyageReportsStore.foresightRequestsForVoyage : null;
    },
    vesselTankStatuses() {
      return this.voyageReport ? this.getVesselTankStatuses(this.voyageReport.vesselId) : null;
    },
    invalidActivitiesCount() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.invalidActivities.length : 0;
    },
    disableEndVoyage() {
      const isActivitiesLogged = this.getSyncedAndUnSyncedVesselActivities
        ? this.getSyncedAndUnSyncedVesselActivities.length > 0 : false;

      return !this.isClientConnectedToServer || !isActivitiesLogged;
    },
    isSafetyObservationSiteEnabled() {
      return this.getActiveSiteConfig && this.getActiveSiteConfig.safetyObservationsEnabled;
    }
  },
  watch: {
    voyageReport(newVal) {
      this.editReport = { ...newVal };
    },
  },
  methods: {
    closeDetailsDialog() {
      this.editReport = { ...this.voyageReport };
      this.setReportDateAndTimeValues();
      this.bus.$emit('resetForm');
      this.detailsDialog = false;
    },
    closeStartVoyageDialog() {
      // this.editReport = { ...this.voyageReport };
      this.startVoyageDialog = false;
    },
    closeEndVoyageDialog() {
      this.editReport = { ...this.voyageReport };
      this.endVoyageDialog = false;

      this.$nextTick(() => {
        this.setEndVoyageDialogDefaultValues();
      });
    },
    openEditVoyageDialog() {
      this.setReportDateAndTimeValues();
      this.detailsDialog = true;
    },
    openStartVoyageDialog() {
      this.startVoyageDialog = true;
    },
    openEndVoyageDialog() {
      this.endVoyageDialog = true;
    },
    openEndVoyageDischargeDialog() {
      this.endVoyageDialogTitle = 'Discharge Recorded: End Voyage?';
      this.endVoyageDialogMessage = 'A Discharge activity has been recorded. Would you like to end the voyage?';
      this.endVoyageDialog = true;
    },
    editVoyageReport() {
      this.isUpdateSubmitting = true;

      const reportUpdateModel = {
        ...this.editReport,
        initialPort: this.editReport.initialPortLocation.name,
      };

      this.$store.dispatch('voyageReportsStore/updateVoyageReport', reportUpdateModel)
        .then((response) => { this.onSuccess('Voyage Report Updated', response); })
        .then(() => {
          if (this.detailsDialog) this.closeDetailsDialog();
          else if (this.startVoyageDialog) this.closeStartVoyageDialog();
          else if (this.endVoyageClicked) this.closeEndVoyageDialog();
        })
        .catch((err) => {
          this.onError('Failed to Update Voyage Report', err);
        })
        .finally(() => { this.isUpdateSubmitting = false; });
    },
    deleteVoyageReport() {
      this.$store.dispatch('voyageReportsStore/deleteVoyageReport', this.voyageReport.id)
        .then((resp) => {
          this.onSuccess('Voyage Report deleted', true);
          this.$router.push('/voyage-reports');
        })
        .catch((err) => {
          this.onError('Failed to delete Voyage Report');
          console.error(err);
        });
    },
    endVoyageClicked() {
      // Set VoyageEndDateTimeLt to last activity EndDateTimeLt
      const currentActivity = this.getSyncedAndUnSyncedVesselActivities
        ? this.getSyncedAndUnSyncedVesselActivities[0] : null;

      if (currentActivity && currentActivity.endDateTimeLt) {
        const endDateTimeString = currentActivity.endDateTimeLt;
        this.editReport.voyageEndDateTimeLt = endDateTimeString;
        this.editVoyageReport();
      } else {
        const msg = 'Failed to End Voyage: No valid activities logged for voyage';
        this.onError(msg, msg);
      }
    },
    setEndVoyageDialogDefaultValues() {
      this.endVoyageDialogTitle = this.defaultEndVoyageDialogTitle;
      this.endVoyageDialogMessage = this.defaultEndVoyageDialogMessage;
    },
    setReportDateAndTimeValues() {
      const {
        voyageStartDateTimeLt,
        voyageEndDateTimeLt,
        ataDateTimeLt,
        atdDateTimeLt,
      } = this.editReport;

      if (voyageStartDateTimeLt) {
        this.editReport.startDate = dateTimeHelpers
          .getDateStringFromDateTime(voyageStartDateTimeLt);
        this.editReport.startTime = dateTimeHelpers
          .getTimeStringFromDateTime(voyageStartDateTimeLt);
      }

      if (voyageEndDateTimeLt) {
        this.editReport.endDate = dateTimeHelpers
          .getDateStringFromDateTime(voyageEndDateTimeLt);
        this.editReport.endTime = dateTimeHelpers
          .getTimeStringFromDateTime(voyageEndDateTimeLt);
      }

      if (ataDateTimeLt) {
        this.editReport.ataDate = dateTimeHelpers
          .getDateStringFromDateTime(ataDateTimeLt);
        this.editReport.ataTime = dateTimeHelpers
          .getTimeStringFromDateTime(ataDateTimeLt);
      }

      if (atdDateTimeLt) {
        this.editReport.atdDate = dateTimeHelpers
          .getDateStringFromDateTime(atdDateTimeLt);
        this.editReport.atdTime = dateTimeHelpers
          .getTimeStringFromDateTime(atdDateTimeLt);
      }
    },
    onSuccess(message, voyageDeleted) {
      this.$toast.success(message);
    },
    onError(message, err) {
      this.$toast.error(message);
      console.error(err);
    },
  },
  beforeDestroy() {
    this.$store.dispatch('voyageReportsStore/clearVoyageData');
  },
};
</script>

<style scoped>
.details-container {
  display: block;
  padding: 16px 16px 32px 16px;
}
.details-container .value-container {
  margin: auto;
  max-width: 250px;
  text-align: start;
  margin-bottom: 8px;
}

@media only screen and (min-width: 960px) {
  .details-container {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
  }
  .details-container .value-container {
    margin: 0 16px;
    max-width: none;
    text-align: center;
  }
}
</style>
